import React, { useEffect, useState } from 'react';
import { View, Animated, type LayoutChangeEvent } from 'react-native';
import colors from '~/assets/themes/colors';

interface Step {
  size: number;
  index: number;
  scroll: Animated.Value;
  screenWidth: number;
  inactiveColor?: string;
  activeColor?: string;
}

const Step: React.FC<Step> = ({
  size,
  index,
  scroll,
  inactiveColor,
  activeColor,
  screenWidth,
}) => {
  const [sizes, setSizes] = useState<{
    min: number;
    max: number;
  }>({
    min: 0,
    max: 0,
  });

  useEffect(() => {
    setSizes({
      min: screenWidth * index - screenWidth,
      max: screenWidth * index,
    });
  }, [index, screenWidth]);

  const width = scroll.interpolate({
    inputRange: [sizes.min, sizes.max, sizes.max + screenWidth],
    outputRange: [size, 20, size],
    extrapolate: 'clamp',
  });

  const color = scroll.interpolate({
    inputRange: [sizes.min, sizes.max, sizes.max + screenWidth],
    outputRange: [
      inactiveColor ?? colors.light[100],
      activeColor ?? colors.primary[600],
      inactiveColor ?? colors.light[100],
    ],
    extrapolate: 'clamp',
  });

  return (
    <Animated.View
      style={{
        width: width,
        height: size,
        borderRadius: 4,
        marginHorizontal: 4,
        backgroundColor: color,
      }}
    />
  );
};

interface Props {
  stepSize: number;
  stepsNumber: number;
  scroll: Animated.Value;
  inactiveColor?: string;
  activeColor?: string;
  screenWidth: number;
  onLayout?(event: LayoutChangeEvent): void;
}

const Steps: React.FC<Props> = ({
  stepSize,
  stepsNumber,
  scroll,
  inactiveColor,
  activeColor,
  screenWidth,
  onLayout,
}) => {
  const [steps, setSteps] = useState<JSX.Element[] | undefined>([]);

  useEffect(() => {
    const newSteps: JSX.Element[] = [];

    for (let i = 0; i < stepsNumber; i++) {
      newSteps.push(
        <Step
          key={i}
          index={i}
          scroll={scroll}
          size={stepSize}
          inactiveColor={inactiveColor}
          activeColor={activeColor}
          screenWidth={screenWidth}
        />
      );
    }
    setSteps(newSteps);
  }, [activeColor, inactiveColor, screenWidth, scroll, stepSize, stepsNumber]);

  return (
    <View onLayout={onLayout} className="flex-row">
      {steps}
    </View>
  );
};

export default Steps;
